import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

const SENTINEL = '••••••••';

type FormState = {
  accessToken: string;
  phoneNumberId: string;
  appSecret: string;
  verifyToken: string;
  apiVersion: string;
  businessPhone: string;
};

const EMPTY: FormState = {
  accessToken: '', phoneNumberId: '', appSecret: '',
  verifyToken: '', apiVersion: '', businessPhone: '',
};

export function WhatsappConfigSection() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const configQuery = useQuery({
    queryKey: ['system-config-whatsapp'],
    queryFn: () => api.get<FormState>('/system-config/whatsapp'),
  });

  useEffect(() => {
    if (configQuery.data) setForm(configQuery.data);
  }, [configQuery.data]);

  function handleChange(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  function handleSecretFocus(field: keyof FormState) {
    return () => {
      if (form[field] === SENTINEL) setForm((f) => ({ ...f, [field]: '' }));
    };
  }

  function handleSecretBlur(field: keyof FormState) {
    return () => {
      if (form[field] === '' && configQuery.data?.[field] === SENTINEL) {
        setForm((f) => ({ ...f, [field]: SENTINEL }));
      }
    };
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg(null);
    try {
      await api.patch('/system-config/whatsapp', form);
      queryClient.invalidateQueries({ queryKey: ['system-config-whatsapp'] });
      setSaveMsg({ ok: true, text: 'Saved successfully.' });
    } catch {
      setSaveMsg({ ok: false, text: 'Save failed. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card p-4 space-y-4">
      <header className="flex items-center justify-between border-b border-ink-200 pb-3">
        <div>
          <h2 className="text-sm font-semibold text-ink-900">WhatsApp (Meta Cloud API)</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Credentials saved here take effect immediately — no restart required.
          </p>
        </div>
      </header>

      {configQuery.isLoading && (
        <p className="text-sm text-ink-400">Loading…</p>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="label">Access Token</label>
          <input
            className="input"
            type="password"
            value={form.accessToken}
            onChange={handleChange('accessToken')}
            onFocus={handleSecretFocus('accessToken')}
            onBlur={handleSecretBlur('accessToken')}
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="label">Phone Number ID</label>
          <input
            className="input"
            value={form.phoneNumberId}
            onChange={handleChange('phoneNumberId')}
            placeholder="123456789012345"
          />
        </div>
        <div>
          <label className="label">App Secret</label>
          <input
            className="input"
            type="password"
            value={form.appSecret}
            onChange={handleChange('appSecret')}
            onFocus={handleSecretFocus('appSecret')}
            onBlur={handleSecretBlur('appSecret')}
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="label">Verify Token</label>
          <input
            className="input"
            type="password"
            value={form.verifyToken}
            onChange={handleChange('verifyToken')}
            onFocus={handleSecretFocus('verifyToken')}
            onBlur={handleSecretBlur('verifyToken')}
            placeholder="••••••••"
          />
        </div>
        <div>
          <label className="label">Business Phone (E.164)</label>
          <input
            className="input"
            value={form.businessPhone}
            onChange={handleChange('businessPhone')}
            placeholder="+447911123456"
          />
        </div>
        <div>
          <label className="label">API Version</label>
          <input
            className="input"
            value={form.apiVersion}
            onChange={handleChange('apiVersion')}
            placeholder="v22.0"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saveMsg && (
          <p className={`text-sm ${saveMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
            {saveMsg.text}
          </p>
        )}
      </div>
    </section>
  );
}
